import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from 'src/components/ui/alert';
import { Button } from 'src/components/ui/button';

type RetryQueryAlertProps = {
  queryKey: QueryKey;
  message?: string;
  isLoading: boolean;
  onRetry?: () => void;
};

export function RetryQueryAlert({
  queryKey,
  message,
  isLoading,
  onRetry,
}: RetryQueryAlertProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('errors');

  return (
    <Alert variant='destructive'>
      <AlertCircle className='h-4 w-4' />
      <AlertDescription className='flex items-center justify-between'>
        <span>{message || t('Failed to fetch data')}</span>
      </AlertDescription>
      <div className='mt-3'>
        <Button
          className='h-8 bg-transparent text-error-default'
          disabled={isLoading}
          onClick={() => {
            queryClient.invalidateQueries({ queryKey });
            onRetry?.();
          }}
          size='sm'
          variant='outline'
        >
          <RefreshCw className='mr-2 h-3 w-3' />
          {t('Try again')}
        </Button>
      </div>
    </Alert>
  );
}
